import { Button, Card, Flex, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { SalesCall1LeadContactInfo, CaseInfo, PreSalesQuestionnaireContext } from '../../../dto/model';
import { SalesCall1LeadContactEditModal } from '../modal/sales-call1-lead-contact-edit-modal';
import { strings } from '../../../lang';

dayjs.extend(utc);
dayjs.extend(timezone);

const DISPLAY_VALUE_FALLBACK = '—';

export const formatPreSalesCustomerName = (
  customer: Pick<CaseInfo, 'salutation' | 'firstName' | 'lastName'>,
) =>
  [customer.salutation, customer.firstName, customer.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ') || DISPLAY_VALUE_FALLBACK;

export const formatPreSalesAddressLines = (
  customer: Pick<CaseInfo, 'street' | 'houseNumber' | 'zipcode' | 'city'>,
) => {
  const streetLine = [customer.street, customer.houseNumber]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const cityLine = [customer.zipcode, customer.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return [streetLine, cityLine].filter(Boolean);
};

// The leads store falls back to DateTime.MinValue when the lead event carries no creation time.
export const formatPreSalesSignupDate = (createdDate?: Date | string | null) => {
  const date = createdDate ? dayjs.utc(createdDate) : null;
  return date?.isValid() && date.year() >= 1970
    ? date.tz('Europe/Berlin').format('DD.MM.YYYY')
    : DISPLAY_VALUE_FALLBACK;
};

type PreSalesDealInfoCardProps = {
  caseInfo: CaseInfo;
  context?: PreSalesQuestionnaireContext;
  isAdCase?: boolean;
  contactEditDisabled?: boolean;
  onContactSaved?: (contact: SalesCall1LeadContactInfo) => void;
  onContactBusyChange?: (busy: boolean) => void;
};

const displayValue = (value?: string) => value?.trim() || DISPLAY_VALUE_FALLBACK;
const definitionValueStyle: CSSProperties = { margin: 0, overflowWrap: 'anywhere' };
const { Text } = Typography;

export const PreSalesDealInfoCard = ({
  caseInfo,
  context,
  isAdCase = false,
  contactEditDisabled,
  onContactSaved,
  onContactBusyChange,
}: PreSalesDealInfoCardProps) => {
  const addressLines = formatPreSalesAddressLines(caseInfo);
  const [editing, setEditing] = useState(false);

  return (
    <Card aria-labelledby="bp-presales-deal-info-title" size='small'>
      <Flex vertical gap={16}>
        <Flex justify="space-between" align="center" gap={8}>
          <Text id="bp-presales-deal-info-title" strong style={{ margin: 0 }} type='secondary'>
            {strings.preSalesQuestionnaire.dealInfoTitle.toUpperCase()}
          </Text>
          {isAdCase && onContactSaved && <Button aria-label={strings.edit} icon={<EditOutlined />} disabled={contactEditDisabled} onClick={() => setEditing(true)}>{strings.edit}</Button>}
        </Flex>
        <Flex component="dl" vertical gap={12} style={{ margin: 0 }}>
          {!isAdCase && (
            <div>
              <dt><Text type="secondary">{strings.preSalesQuestionnaire.dealIdLabel}</Text></dt>
              <dd style={definitionValueStyle}>
                <Text strong>{displayValue(context?.crmId)}</Text>
              </dd>
            </div>
          )}
          <div>
            <dt><Text type="secondary">{isAdCase ? strings.adBookingPage.signupIdLabel : strings.preSalesQuestionnaire.hpOrderNumberLabel}</Text></dt>
            <dd style={definitionValueStyle}>
              <Text strong>{displayValue(isAdCase ? caseInfo.signupId : context?.hpOrderNumber)}</Text>
            </dd>
          </div>
          {isAdCase && (
            <Flex gap={24} wrap>
              <div>
                <dt><Text type="secondary">{strings.adBookingPage.signupDateLabel}</Text></dt>
                <dd style={definitionValueStyle}>
                  <Text strong>{formatPreSalesSignupDate(caseInfo.createdDate)}</Text>
                </dd>
              </div>
              <div>
                <dt><Text type="secondary">{strings.adBookingPage.callCounterLabel}</Text></dt>
                <dd style={definitionValueStyle}>
                  <Text strong>{caseInfo.callCounter ?? DISPLAY_VALUE_FALLBACK}</Text>
                </dd>
              </div>
            </Flex>
          )}
          {/* The unified (type=ad) flow does not show the address in the deal info card. */}
          {!isAdCase && (
            <div>
              <dt><Text type="secondary">{strings.preSalesQuestionnaire.addressLabel}</Text></dt>
              <dd style={definitionValueStyle}>
                {addressLines.length > 0
                  ? (
                    <Flex vertical>
                      {addressLines.map((line) => <Text key={line} strong>{line}</Text>)}
                    </Flex>
                  )
                  : <Text strong>{DISPLAY_VALUE_FALLBACK}</Text>}
              </dd>
            </div>
          )}
          <div>
            <dt><Text type="secondary">{strings.preSalesQuestionnaire.emailLabel}</Text></dt>
            <dd style={definitionValueStyle}>
              <Text strong>{displayValue(caseInfo.email)}</Text>
            </dd>
          </div>
        </Flex>
      </Flex>
      {isAdCase && editing && onContactSaved && <SalesCall1LeadContactEditModal caseInfo={caseInfo} disabled={contactEditDisabled}
        onCancel={() => setEditing(false)} onBusyChange={onContactBusyChange}
        onSaved={(contact) => { onContactSaved(contact); setEditing(false); }} />}
    </Card>
  );
};
